import React from 'react';
import { useSelector } from 'react-redux';
import { Star, Award, Briefcase, Mail, Phone, Globe, Calendar } from 'lucide-react';

const TrainerDetailsView = () => {
    const { trainerList: trainer } = useSelector((state) => state.trainers);

    if (!trainer?.data?.[0]) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                <p className="text-secondary fs-5">Loading trainer details...</p>
            </div>
        );
    }
    
    const trainerData = trainer.data[0];

    const formatDate = (date) => new Date(date).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric'
    });

    return (
        <div className="card shadow-sm mb-4 mt-2">
            {/* Header */}
            <div className="card-header bg-primary text-white rounded-top p-4">
                <div className="row align-items-center">
                    {trainerData.image ? (
                        <div className="col-auto">
                            <img
                                src={trainerData.image}
                                alt={trainerData.full_name}
                                className="rounded-circle"
                                style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                            />
                        </div>
                    ) : (
                        <div className="col-auto">
                            <div className="bg-light rounded-circle d-flex justify-content-center align-items-center" 
                                 style={{ width: '100px', height: '100px' }}>
                                <span className="fs-1 text-primary fw-bold">{trainerData.full_name?.charAt(0)}</span>
                            </div>
                        </div>
                    )}
                    <div className="col">
                        <h2 className="fw-bold mb-1 text-white">{trainerData.full_name}</h2>
                        <div className="d-flex align-items-center">
                            <Star className="text-warning me-1" />
                            <span className="fw-semibold">
                                {(trainerData.avg_review)} ({trainerData.total_review} reviews)
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Stats */}
            <div className="card-body">
                <div className="row mb-4">
                    <div className="col-lg-8">
                        <h5 className="fw-semibold border-bottom pb-2 mb-3">👨‍🏫 Trainer Profile</h5>
                        <div className="row mb-3">
                            <div className="col-sm-6 mb-2 d-flex align-items-start">
                                <Mail className="me-2 text-primary" />
                                <div>
                                    <small className="text-muted">Email</small><br />
                                    <strong>{trainerData.email}</strong>
                                </div>
                            </div>
                            <div className="col-sm-6 mb-2 d-flex align-items-start">
                                <Phone className="me-2 text-primary" />
                                <div>
                                    <small className="text-muted">Phone</small><br />
                                    <strong>+{trainerData.country_code} {trainerData.phone_number}</strong>
                                </div>
                            </div>
                            <div className="col-sm-6 mb-2 d-flex align-items-start">
                                <Briefcase className="me-2 text-primary" />
                                <div>
                                    <small className="text-muted">Experience</small><br />
                                    <strong>{trainerData.experience} years</strong>
                                </div>
                            </div>
                            <div className="col-sm-6 mb-2 d-flex align-items-start">
                                <Globe className="me-2 text-primary" />
                                <div>
                                    <small className="text-muted">Industry</small><br />
                                    <strong>{trainerData.industry || 'Not specified'}</strong>
                                </div>
                            </div>
                            <div className="col-sm-6 mb-2 d-flex align-items-start">
                                <Calendar className="me-2 text-primary" />
                                <div>
                                    <small className="text-muted">Registered On</small><br />
                                    <strong>{formatDate(trainerData.createdAt)}</strong>
                                </div>
                            </div>
                        </div>

                        {/* Certifications */}
                        <h5 className="fw-semibold border-bottom pb-2 mb-3">🏆 Certifications</h5>
                        {trainerData.trainer_certification?.length > 0 ? (
                            <div className="row">
                                {trainerData.trainer_certification.map(cert => (
                                    <div key={cert.id} className="col-md-6 mb-3">
                                        <div className="card h-100 border-0 shadow-sm">
                                            <div className="card-body">
                                                <div className="d-flex align-items-center mb-2">
                                                    <Award className="text-primary me-2" />
                                                    <h6 className="mb-0 fw-semibold">{cert.name}</h6>
                                                </div>
                                                <p className="text-muted small mb-2">Institution: {cert.institution}</p>
                                                {cert.certificate_image && (
                                                    <a href={cert.certificate_image} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-primary">
                                                        View Certificate
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-muted">No certifications available.</p>
                        )}
                    </div>

                    {/* Education */}
                    <div className="col-lg-4">
                        <h5 className="fw-semibold border-bottom pb-2 mb-3">🎓 Education</h5>
                        {trainerData.trainer_education?.length > 0 ? (
                            <div>
                                {trainerData.trainer_education.map(edu => (
                                    <div key={edu.id} className="card mb-3 border-0 shadow-sm">
                                        <div className="card-body">
                                            <h6 className="mb-1 fw-bold">{edu.name}</h6>
                                            <p className="text-muted small mb-1">{edu.university}</p>
                                            <div className="d-flex align-items-center small">
                                                <span className="badge bg-success me-2">{edu.marks_in_percentage}%</span>
                                            </div>
                                            {edu.certificate_image && (
                                                <a href={edu.certificate_image} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-primary mt-2">
                                                    View Certificate
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-muted">No education details available.</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TrainerDetailsView;